import { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Paper,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ThemeContext } from '../Context';
import { LeftPanelContext } from '../Context';
import { RubricDragContext } from '../Context';
import axios from 'axios';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
import { insertRubricsBefore } from '../../utils/rubricDrop';

function StandardReports() {
  const [standards, setStandards] = useState([]);
  const [filteredStandards, setFilteredStandards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddingStandard, setIsAddingStandard] = useState(false);
  const [processingStandardId, setProcessingStandardId] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const { highlightTarget } = useContext(LeftPanelContext);
  const { startRubricDrag } = useContext(RubricDragContext);
  const [guideHighlight, setGuideHighlight] = useState(false);
  const lastActionRef = useRef({
    type: null,
    id: null,
    timestamp: null
  });
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Contour pulsant temporaire déclenché par le guide de démarrage
  useEffect(() => {
    if (!highlightTarget || highlightTarget.view !== 'standardReports') return;
    setGuideHighlight(true);
    const timer = setTimeout(() => setGuideHighlight(false), 4000);
    return () => clearTimeout(timer);
  }, [highlightTarget]);

  useEffect(() => {
    fetchStandards();
  }, []);

  useEffect(() => {
    const filtered = standards.filter(standard =>
      standard.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredStandards(filtered);
  }, [searchQuery, standards]);

  const fetchStandards = async () => {
    try {
      const response = await fetch('/api/standards');
      const data = await response.json();
      setStandards(data);
      setFilteredStandards(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des canevas');
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({...notification, open: false});
  };

  // beforeRubricId : rubrique devant laquelle insérer (glisser-déposer), null = en fin de document
  const handleAddStandard = async (standard, beforeRubricId = null) => {
    // Vérification qu'un rapport est ouvert
    if (!report) {
      setNotification({
        open: true,
        message: 'Aucun rapport n\'est actuellement ouvert',
        severity: 'error'
      });
      return;
    }

    try {
      setIsAddingStandard(true);
      setProcessingStandardId(standard.id);
      const token = sessionStorage.getItem('authToken');
      
      // Récupérer les données actuelles du rapport
      const reportResponse = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!reportResponse.data) {
        throw new Error('Impossible de récupérer les données du rapport');
      }
      
      const reportId = reportResponse.data._id;
      const currentRubrics = reportResponse.data.rubrics || [];
      
      // Récupérer les données complètes des rubriques du PV standard
      const rubricsResponse = await axios.get('/api/standards/rubrics', {
        params: { includeObsolete: true }
      });
      const allRubrics = rubricsResponse.data;
      
      // Filtrer les rubriques associées à ce standard
      const standardRubrics = standard.rubrics.map(rubricId => {
        const rubric = allRubrics.find(r => r.id === rubricId);
        return rubric;
      }).filter(r => r !== undefined);
      
      // Créer les nouvelles rubriques
      const newRubrics = standardRubrics.map((rubric, index) => {
        let newRubric = {
          id: `rubric-${Date.now()}-${Math.floor(Math.random() * 10000)}-${index}`,
          title: rubric.title,
          isEditing: false,
          editedBy: null
        };
        
        // Appliquer les propriétés spécifiques selon le type
        if (rubric.type === "intelligent") {
          newRubric = {
            ...newRubric,
            content: "",
            version: rubric.version || "",
            generated: false
          };
        } else if (rubric.type === "manual") {
          newRubric = {
            ...newRubric,
            content: rubric.content || "",
            version: null,
            generated: true
          };
        } else {
          // Cas par défaut si type non spécifié
          newRubric = {
            ...newRubric,
            content: rubric.content || "",
            version: "",
            generated: true
          };
        }
        
        return newRubric;
      });
      
      // Insérer les nouvelles rubriques dans la liste existante
      const updatedRubrics = insertRubricsBefore(currentRubrics, newRubrics, beforeRubricId);
      const insertedPosition = newRubrics.length
        ? updatedRubrics.find(r => r.id === newRubrics[0].id).position
        : null;
      
      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/rubrics`,
        { rubrics: updatedRubrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        // Émettre deux événements pour s'assurer que les mises à jour sont reçues
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          rubrics: updatedRubrics
        });
        
        socket.emit('broadcastReportUpdate', {
          number: report.number,
          year: report.year,
          rubrics: updatedRubrics
        });
      }
      
      // Enregistrer l'action pour éviter les doublons de traitement
      lastActionRef.current = {
        type: 'add_standard',
        id: standard.id,
        timestamp: Date.now()
      };
      
      // Notification de succès
      setNotification({
        open: true,
        message: `Canevas "${standard.title}" ajouté avec succès`,
        severity: 'success'
      });

      // Log de l'ajout du groupe de rubriques
      logAction(LOG_ACTIONS.RUBRIC_ADD_GROUP, formatReportNumber(report.number, report.year), {
        standardId: standard.id,
        standardTitle: standard.title,
        rubricsCount: newRubrics.length,
        rubricTitles: newRubrics.map(r => r.title),
        position: insertedPosition
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout du canevas:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de l\'ajout du canevas',
        severity: 'error'
      });
    } finally {
      setIsAddingStandard(false);
      setProcessingStandardId(null);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      backgroundColor: isDark ? '#1a252f' : 'inherit',
      ...(guideHighlight && {
        animation: 'panelGuidePulse 1s ease-in-out infinite',
        '@keyframes panelGuidePulse': {
          '0%, 100%': { boxShadow: 'inset 0 0 0 0 rgba(25, 118, 210, 0)' },
          '50%': {
            boxShadow: isDark
              ? 'inset 0 0 0 3px rgba(79, 195, 247, 0.55)'
              : 'inset 0 0 0 3px rgba(25, 118, 210, 0.45)'
          }
        }
      })
    }}>
      <Box sx={{ 
        p: 0.5, 
        borderBottom: 1, 
        borderColor: isDark ? '#2d3f4c' : 'divider'
      }}>
        <Typography variant="caption" sx={{ 
          pl: 1, 
          fontSize: '0.8rem', 
          fontWeight: 'bold', 
          display: 'block', 
          mb: 0.5,
          color: isDark ? '#4fc3f7' : 'inherit'
        }}>
          Ajouter un canevas complet
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Rechercher un canevas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ 
            '& .MuiInputBase-root': { 
              height: 32,
              fontSize: '0.8rem',
              backgroundColor: isDark ? '#22303e' : 'inherit',
              color: isDark ? '#ecf0f1' : 'inherit',
              '& fieldset': {
                borderColor: isDark ? '#2d3f4c' : undefined
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#4fc3f7' : undefined
              },
              '&.Mui-focused fieldset': {
                borderColor: isDark ? '#4fc3f7' : undefined
              }
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              color: isDark ? '#ecf0f1' : 'inherit'
            },
            '& .MuiInputAdornment-root .MuiSvgIcon-root': {
              color: isDark ? '#95a5a6' : undefined
            }
          }}
        />
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            m: 0.5, 
            py: 0, 
            fontSize: '0.75rem',
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ff6b6b' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ff6b6b' : undefined
            }
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <List 
          dense 
          disablePadding 
          sx={{ 
            flex: 1, 
            overflowY: 'auto',
            backgroundColor: isDark ? '#1a252f' : 'inherit',
            '& .MuiListItem-root': {
              py: 0
            },
          }}
        >
          {filteredStandards.map((standard) => (
            <ListItem
              key={standard.id}
              draggable={!isAddingStandard}
              onDragStart={(event) => startRubricDrag(
                event,
                standard.title,
                (beforeRubricId) => handleAddStandard(standard, beforeRubricId)
              )}
              sx={{
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: isDark ? '#2d3f4c' : 'divider',
                minHeight: 32,
                backgroundColor: isDark ? '#22303e' : 'inherit',
                cursor: isAddingStandard ? 'default' : 'grab',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'action.hover',
                }
              }}
            >
              <DragIndicatorIcon sx={{
                fontSize: 16,
                mr: 0.5,
                flexShrink: 0,
                color: isDark ? '#95a5a6' : 'text.secondary',
                opacity: isAddingStandard ? 0.35 : 1
              }} />
              <ListItemText 
                primary={standard.title}
                secondary={`${standard.rubrics.length} rubrique${standard.rubrics.length > 1 ? 's' : ''}`}
                primaryTypographyProps={{
                  sx: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.8rem',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }
                }}
                secondaryTypographyProps={{
                  sx: {
                    fontSize: '0.7rem',
                    color: isDark ? '#95a5a6' : 'inherit'
                  }
                }}
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  color="primary"
                  size="small"
                  onClick={() => handleAddStandard(standard)}
                  disabled={isAddingStandard}
                  sx={{ padding: 0.5 }}
                >
                  {isAddingStandard && processingStandardId === standard.id ? 
                    <CircularProgress size={16} /> : 
                    <AddCircleOutlineIcon fontSize="small" />
                  }
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {filteredStandards.length === 0 && !loading && (
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.75rem',
                  color: isDark ? '#95a5a6' : 'text.secondary'
                }}
              >
                {searchQuery ? 'Aucun PV canevas trouvé' : 'Aucun PV canevas disponible'}
              </Typography>
            </Box>
          )}
        </List>
      )}

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ 
            width: '100%', 
            fontSize: '0.8rem', 
            py: 0.5,
            backgroundColor: isDark 
              ? (notification.severity === 'success' 
                  ? 'rgba(76, 175, 80, 0.15)' 
                  : 'rgba(244, 67, 54, 0.15)')
              : undefined,
            color: isDark 
              ? (notification.severity === 'success' 
                  ? '#4ecdc4' 
                  : '#ff6b6b')
              : undefined,
            '& .MuiAlert-icon': {
              color: isDark 
                ? (notification.severity === 'success' 
                    ? '#4ecdc4' 
                    : '#ff6b6b')
                : undefined
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default StandardReports;



